import { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { SellerSidebar } from '@/components/seller/SellerSidebar';
import DashboardHeader from '@/components/DashboardHeader';
import { ShopProvider } from '@/contexts/ShopContext';
import { ShopInitializer } from '@/components/seller/ShopInitializer';
import { usePanelLocale } from '@/hooks/usePanelLocale';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

interface SellerLayoutProps {
  children: ReactNode;
}

/**
 * SellerLayout - App shell layout for seller dashboard
 * - Sticky top header (Navigation)
 * - Fixed left sidebar
 * - Scrollable main content area
 * 
 * IMPORTANT: Seller panel is ALWAYS English + LTR by business decision.
 * DO NOT modify this behavior when adding new locales.
 */
export function SellerLayout({ children }: SellerLayoutProps) {
  // Force English + LTR for seller panel
  usePanelLocale();
  
  const navigate = useNavigate();
  const location = useLocation();
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (cancelled) return;
        
        if (!user) {
          // Redirect to auth with return URL so user comes back after login
          const returnUrl = location.pathname + location.search;
          navigate(`/auth?redirect=${encodeURIComponent(returnUrl)}`, { replace: true });
          return;
        }
        
        setIsAuthenticated(true);
      } catch (error) {
        console.error('[SellerLayout] Auth check failed:', error);
        navigate('/auth', { replace: true });
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    };

    checkAuth();

    return () => {
      cancelled = true;
    };
  }, [navigate, location.pathname, location.search]);

  // Show loading while checking auth
  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Don't render anything if not authenticated (redirect is in progress)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <ShopProvider>
      <ShopInitializer />
      <SidebarProvider defaultOpen={false}>
        {/* 
          Seller Panel: Always English + LTR 
          The force-ltr class ensures proper direction even if parent has RTL
        */}
        <div className="flex min-h-screen w-full bg-background force-ltr" dir="ltr" lang="en">
          <SellerSidebar />
          
          <SidebarInset className="flex-1 flex flex-col w-full">
            <DashboardHeader title="Seller Dashboard" />
            
            <main className="flex-1 overflow-y-auto">
              <div className="container mx-auto p-4 sm:p-6 max-w-full">
                {children}
              </div>
            </main>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </ShopProvider>
  );
}
